/*global google*/

import React, { useCallback, useEffect, useState } from 'react';
import styles from './PlaceOrder.module.css';
import { useDispatch } from 'react-redux';
import { Alert } from '@material-ui/lab';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

import { phoneUtil } from '../../app/utils';
import { createAddress, updateAddress } from '../address/addressAPI';
import { getAllSavedAddresses, getPlaceLongName } from './placeOrderSlice';

const AUTOCOMPLETE_ID = "addressFormAutocomplete";

export const AddressForm = (props) => {
  const { type, address, onCancel, onSaved } = props;
  const dispatch = useDispatch();

  const isPickUp = type === "PICK_UP";

  const [bookmarkName, setBookmarkName] = useState(address ? address.bookmarkName : "");
  const [personName, setPersonName] = useState(address ? (isPickUp ? address.pickUpPersonName : address.recipientName) : "");
  const [contactNumber, setContactNumber] = useState(
    address ? (isPickUp ? address.pickUpContactNumber : address.recipientPhone).replace("+", "") : ""
  );
  const [unitNumber, setUnitNumber] = useState(address ? address.location.unitNumber : "");
  const [addressText, setAddressText] = useState(address ? address.location.address : "");
  const [location, setLocation] = useState(address ? address.location : null);
  const [autocompleteInit, setAutocompleteInit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const onPlaceChanged = useCallback((autocomplete) => {
    const place = autocomplete.getPlace();
    if (!place) return;

    try {
      setAddressText(`${place.name}, ${place.formatted_address}`);
      setLocation({
        address: `${place.name}, ${place.formatted_address}`,
        coordinate: {
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
        },
        street: getPlaceLongName(place.address_components, "street_address"),
        apartment: getPlaceLongName(place.address_components, "street_address"),
        state: getPlaceLongName(place.address_components, "administrative_area_level_1"),
        postalCode: getPlaceLongName(place.address_components, "postal_code"),
        city: getPlaceLongName(place.address_components, "locality"),
        country: getPlaceLongName(place.address_components, "country")
      });
      setError(false);
      setErrorMessage("");
    } catch (e) {
      setError(true);
      setErrorMessage("Please select an address from the drop-down list.");
    }
  }, []);

  useEffect(() => {
    const autocompleteInput = document.getElementById(AUTOCOMPLETE_ID);
    if (!autocompleteInit && autocompleteInput !== null) {
      const autocomplete = new google.maps.places.Autocomplete(
        autocompleteInput,
        {
          types: ["geocode", "establishment"],
          componentRestrictions: { country: ["my", "sg"] }
        }
      );
      google.maps.event.addListener(autocomplete, "place_changed", () => onPlaceChanged(autocomplete));
      setAutocompleteInit(true);
    }
  }, [autocompleteInit, onPlaceChanged]);

  const submit = async () => {
    if (saving) return;

    if (bookmarkName.trim() === "") {
      setError(true);
      setErrorMessage("Please enter a bookmark name.");
      return;
    }

    if (personName.trim() === "") {
      setError(true);
      setErrorMessage(isPickUp ? "Please enter the pick-up person's name." : "Please enter the recipient's name.");
      return;
    }

    if (contactNumber === "") {
      setError(true);
      setErrorMessage("Please enter a contact number.");
      return;
    }

    const formattedPhoneNumber = "+" + contactNumber.replace(/\s/g, '').replace(/-/g, '');

    try {
      const phoneNumberProto = phoneUtil.parse(formattedPhoneNumber, "");
      if (!phoneUtil.isValidNumber(phoneNumberProto)) {
        setError(true);
        setErrorMessage("Contact number is invalid.");
        return;
      }
    } catch (e) {
      setError(true);
      setErrorMessage("Contact number is invalid.");
      return;
    }

    if (!location || !location.coordinate || location.coordinate.longitude === "" || location.coordinate.longitude === null) {
      setError(true);
      setErrorMessage("Please select an address from the drop-down list.");
      return;
    }

    const payload = isPickUp
      ? {
        jobType: "PICK_UP",
        bookmarkName: bookmarkName,
        pickUpPersonName: personName,
        pickUpContactNumber: formattedPhoneNumber,
        location: { ...location, unitNumber: unitNumber }
      }
      : {
        jobType: "DROP_OFF",
        bookmarkName: bookmarkName,
        recipientName: personName,
        recipientPhone: formattedPhoneNumber,
        location: { ...location, unitNumber: unitNumber }
      };

    setSaving(true);
    try {
      const response = address
        ? await updateAddress(payload, address.id)
        : await createAddress(payload);

      await dispatch(getAllSavedAddresses());
      onSaved(response.data ? response.data : { ...payload, id: address ? address.id : null });
    } catch (e) {
      setError(true);
      setErrorMessage(e?.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.addressFormContainer}>
      {
        error &&
        <Alert
          severity="error"
          style={{ marginBottom: 10, borderRadius: 10, textAlign: "left" }}
          onClose={() => { setError(false); setErrorMessage(""); }}>
          {errorMessage}
        </Alert>
      }

      <input
        id="addressFormBookmarkName"
        name="bookmarkName"
        value={bookmarkName}
        onChange={(e) => setBookmarkName(e.target.value)}
        placeholder="Bookmark name (eg. Warehouse KL)"
        className={styles.addressFormInput}
      />
      <input
        id={AUTOCOMPLETE_ID}
        name="address"
        value={addressText}
        onChange={(e) => setAddressText(e.target.value)}
        placeholder="Search address"
        className={styles.addressFormInput}
      />
      <input
        id="addressFormUnitNumber"
        name="unitNumber"
        value={unitNumber}
        onChange={(e) => setUnitNumber(e.target.value)}
        placeholder="Unit number (optional)"
        className={styles.addressFormInput}
      />
      <input
        id="addressFormPersonName"
        name="personName"
        value={personName}
        onChange={(e) => setPersonName(e.target.value)}
        placeholder={isPickUp ? "Pick-up person's name" : "Recipient's name"}
        className={styles.addressFormInput}
      />
      <div style={{ marginBlock: 6 }}>
        <PhoneInput
          id="addressFormContactNumber"
          name="contactNumber"
          country={'my'}
          countryCodeEditable={false}
          preferredCountries={['my', 'sg', 'au', 'us']}
          autoFormat={false}
          value={contactNumber}
          onChange={(e) => setContactNumber(e)}
          placeholder={isPickUp ? "Pick-up person's contact number" : "Recipient's contact number"}
          inputProps={{ required: true }}
          inputStyle={{ height: 40, fontSize: 12, fontWeight: "500" }}
        />
      </div>

      <div className={styles.addressFormButtons}>
        <div
          className={styles.addressFormButton}
          style={{ backgroundImage: "none", backgroundColor: "white", border: "1px solid #1e9299", boxShadow: "none" }}
          onClick={onCancel}>
          <p className={styles.tealText} style={{ fontSize: 13 }}>Cancel</p>
        </div>
        <div
          className={styles.addressFormButton}
          style={saving ? { backgroundImage: "none", backgroundColor: "#e0e0e0", boxShadow: "none" } : {}}
          onClick={submit}>
          <p className={styles.whiteText} style={{ fontSize: 13 }}>{saving ? "Saving..." : "Save"}</p>
        </div>
      </div>
    </div>
  )
}
